// ---------------------------------------------------------------------------
// Controller for WorkspaceWrapper — owns all hooks, store interactions, and
// side-effects. The view receives only the returned props and renders.
// ---------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";

import { Subscription } from "rxjs";
import {
  selectWorkspace,
  selectWorkspaceId,
  useWorkspaceStore,
  workspaceRefreshSubject
} from "stores/workspace/workspaceStore";

import { useUser } from "hooks";

import useWorkspace from "../hooks/useWorkspace";
import { getWorkspacePermissions } from "../utils";

export interface WorkspaceControllerResult {
  /** Whether the controller is still initialising. Consumers can use this to show loading UI. */
  readonly isLoading: boolean;
}

export function useWorkspaceController(): WorkspaceControllerResult {
  const [refreshTxn, setRefreshTxn] = useState<Maybe<string>>(null);

  // --- Store reads (narrow selectors) ---
  const workspaceId = useWorkspaceStore(selectWorkspaceId);
  const workspace = useWorkspaceStore(selectWorkspace);

  // --- Store actions (stable refs — Zustand guarantees referential stability) ---
  const setWorkspaceId = useWorkspaceStore((s) => s.setWorkspaceId);
  const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
  const setLoading = useWorkspaceStore((s) => s.setLoading);
  const setError = useWorkspaceStore((s) => s.setError);
  const setPermissions = useWorkspaceStore((s) => s.setPermissions);

  // --- External hooks ---
  const { user, isAtLeastPowerUser } = useUser();
  const { data, loading, error, refetch: refetchWorkspace } = useWorkspace(workspaceId);

  // ── Refetch on refresh request ─────────────────────────────────────────
  useEffect(() => {
    if (workspaceId === undefined || workspaceId !== workspace?.workspaceId) return;
    refetchWorkspace();
  }, [refetchWorkspace, workspaceId, refreshTxn, workspace?.workspaceId]);

  // ── Sync loading state ─────────────────────────────────────────────────
  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  // ── Sync error state ───────────────────────────────────────────────────
  useEffect(() => {
    setError(error ?? undefined);
  }, [error, setError]);

  // ── Sync workspace data ────────────────────────────────────────────────
  useEffect(() => {
    if (!data?.workspace) return;

    setWorkspaceId(data.workspace.workspaceId);
    setWorkspace(data.workspace);
  }, [data, setWorkspaceId, setWorkspace]);

  // ── Update permissions when workspace loads ────────────────────────────
  useEffect(() => {
    if (!workspace) return;

    const permissions = getWorkspacePermissions(workspace, user, isAtLeastPowerUser);
    setPermissions(permissions);

    sessionStorage.setItem("workspaceid", workspace.workspaceId);
  }, [workspace, user, isAtLeastPowerUser, setPermissions]);

  // ── Component mounting: restore from session, subscribe to refresh ─────
  const workspaceSubRef = useRef<Maybe<Subscription>>(null);

  useEffect(() => {
    const sessionWorkspaceId = sessionStorage.getItem("workspaceid");

    if (sessionWorkspaceId) {
      setWorkspaceId(sessionWorkspaceId);
    } else {
      setWorkspaceId("");
    }

    // Per-instance subscription — safe under StrictMode double-mount.
    workspaceSubRef.current = workspaceRefreshSubject.subscribe((txn: string) => {
      setRefreshTxn(txn);
    });

    return () => {
      if (workspaceSubRef.current) {
        workspaceSubRef.current.unsubscribe();
        workspaceSubRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLoading: loading };
}
